import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const TYPES = ["asset", "liability", "equity", "revenue", "expense"];

export default function AccountsPage() {
  const { activeCompany, formatApiError } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "asset" });

  const load = async () => {
    if (!activeCompany) return;
    const { data } = await api.get(`/companies/${activeCompany.id}/accounts`);
    setRows(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/companies/${activeCompany.id}/accounts`, form);
      toast.success("Account created");
      setOpen(false);
      setForm({ code: "", name: "", type: "asset" });
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const remove = async (aid) => {
    if (!window.confirm("Delete this account?")) return;
    try {
      await api.delete(`/companies/${activeCompany.id}/accounts/${aid}`);
      toast.success("Deleted");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  return (
    <>
      <PageHeader
        eyebrow="General Ledger"
        title="Chart of Accounts"
        description="The taxonomy of your ledger. Codes drive report ordering; types drive report placement."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-account-btn"
                className="rounded-sm h-9 font-mono uppercase tracking-wider text-xs"
              >
                <Plus size={14} className="mr-2" />
                New Account
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl">
                  New Account
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Code</Label>
                  <Input
                    data-testid="account-code"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    required
                    className="rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Name</Label>
                  <Input
                    data-testid="account-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Type</Label>
                  <Select
                    value={form.type}
                    onValueChange={(v) => setForm({ ...form, type: v })}
                  >
                    <SelectTrigger data-testid="account-type" className="rounded-sm font-mono">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES.map((t) => (
                        <SelectItem key={t} value={t} className="font-mono capitalize">
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    data-testid="account-submit"
                    className="rounded-sm font-mono uppercase tracking-wider text-xs"
                  >
                    Create
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`account-row-${r.code}`}
                  className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors duration-200"
                >
                  <Td className="num font-medium">{r.code}</Td>
                  <Td>{r.name}</Td>
                  <Td>
                    <span className="tag-badge capitalize">{r.type}</span>
                  </Td>
                  <Td className="text-right pr-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(r.id)}
                      data-testid={`account-delete-${r.code}`}
                      className="rounded-sm h-8 text-destructive hover:text-destructive"
                    >
                      <Trash size={14} />
                    </Button>
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-muted-foreground">
                    No accounts yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>
    </>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
